import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Download, CheckCircle, AlertCircle, ExternalLink, FileText, Database, BarChart, Key, FileCode, Settings, Play, ChevronDown, ChevronRight, TrendingUp, Coins, PiggyBank, Upload, RefreshCw, Monitor } from 'lucide-react';
import PortfolioAuditorLogo from './FundWiseLogo';

// Asset Card Component for Phase 2
const AssetCard = ({ title, icon: Icon, iconBgColor, iconColor, children, isExpanded, onToggle }) => {
  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden hover:bg-white/10 transition-all duration-300">
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center">
          <div className={`w-12 h-12 ${iconBgColor} rounded-xl flex items-center justify-center mr-4`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
          <h4 className="font-bold text-lg text-white font-outfit">{title}</h4>
        </div>
        <div className="flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="w-6 h-6 text-slate-400" />
          ) : (
            <ChevronRight className="w-6 h-6 text-slate-500" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-white/10 bg-deep-space-surface/50">
          {children}
        </div>
      )}
    </div>
  );
};

const InstallationGuide = () => {
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [expandedAsset, setExpandedAsset] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  
  // Get installation guide video ID from environment variable
  const installationVideoId = process.env.REACT_APP_INSTALLATION_GUIDE_VIDEO_ID || 'KxgUm5O99W0';

  const togglePhase = (phase) => {
    setExpandedPhase(expandedPhase === phase ? null : phase);
  };

  const toggleAsset = (asset) => {
    setExpandedAsset(expandedAsset === asset ? null : asset);
  };

  return (
    <div className="min-h-screen bg-deep-space">
      {/* Header */}
      <nav className="bg-deep-space/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <PortfolioAuditorLogo size="sm" />
            </Link>
            <Link
              to="/"
              className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-tech-blue to-blue-700 text-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 font-outfit">
            Portfolio Auditor Installation Guide
          </h1>
          <p className="text-lg text-blue-100 max-w-3xl mx-auto font-ibm-plex">
            Complete step-by-step guide to set up Portfolio Auditor on your Windows PC
          </p>
        </div>
      </div>

      {/* Watch Installation Guide Video Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
          {/* Section Header */}
          <div className="bg-gradient-to-r from-tech-blue to-blue-700 text-white px-6 py-3">
            <h2 className="text-xl font-bold flex items-center font-outfit">
              <Play className="w-5 h-5 mr-2" />
              Watch Installation Guide
            </h2>
            <p className="text-blue-100 text-sm mt-1 font-ibm-plex">Follow along with our video tutorial for easy setup</p>
          </div>
          
          {/* Video Container */}
          <div className="p-4">
            <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg bg-black">
              {!showVideo ? (
                // Thumbnail with Play Button Overlay
                <div className="relative w-full h-full">
                  {/* YouTube Thumbnail */}
                  <img
                    src={`https://img.youtube.com/vi/${installationVideoId}/maxresdefault.jpg`}
                    alt="Installation Guide Video Thumbnail"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to hqdefault if maxresdefault is not available
                      e.target.src = `https://img.youtube.com/vi/${installationVideoId}/hqdefault.jpg`;
                    }}
                  />
                  
                  {/* Dark Overlay */}
                  <div className="absolute inset-0 bg-black/30 transition-opacity hover:bg-black/20"></div>
                  
                  {/* Play Button */}
                  <button
                    onClick={() => setShowVideo(true)}
                    className="absolute inset-0 flex items-center justify-center group"
                    aria-label="Play video"
                  >
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-blue-600 rounded-full flex items-center justify-center shadow-2xl transform transition-all duration-300 group-hover:scale-110 group-hover:bg-blue-700">
                      <Play className="w-10 h-10 md:w-12 md:h-12 text-white ml-1" fill="white" />
                    </div>
                  </button>
                  
                  {/* Video Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6">
                    <h3 className="text-white font-semibold text-lg md:text-xl">
                      Portfolio Auditor - Complete Setup Tutorial
                    </h3>
                    <p className="text-gray-300 text-sm mt-1">
                      Learn how to install and configure Portfolio Auditor step by step
                    </p>
                  </div>
                </div>
              ) : (
                // Embedded YouTube Video
                <iframe
                  src={`https://www.youtube.com/embed/${installationVideoId}?autoplay=1&rel=0&modestbranding=1`}
                  title="Portfolio Auditor Installation Guide"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Step-by-Step Written Guide Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section Title */}
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Step-by-Step Written Guide
          </h2>
          <p className="text-slate-600">Detailed instructions for each installation step</p>
        </div>

        {/* Video Reference Note */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-8">
          <div className="flex items-center">
            <Play className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" />
            <p className="text-blue-900 font-medium">
              <span className="font-bold">Note:</span> Refer to the Above Video for Live Demo of complete setup and Asset tracking
            </p>
          </div>
        </div>

        {/* Phase-based Guide */}
        <div className="space-y-6">

          {/* Phase 1: Account & Software Setup */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <button
              onClick={() => togglePhase('phase1')}
              className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center mr-4">
                  <Settings className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-slate-900">Phase 1: Account & Software Setup</h3>
                  <p className="text-sm text-slate-600 mt-1">Steps 1-6: Sign up, download, and install prerequisites</p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-4">
                {expandedPhase === 'phase1' ? (
                  <ChevronDown className="w-6 h-6 text-blue-600" />
                ) : (
                  <ChevronRight className="w-6 h-6 text-slate-400" />
                )}
              </div>
            </button>

            {expandedPhase === 'phase1' && (
              <div className="px-6 py-6 bg-slate-50 border-t border-slate-200">
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">1</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Sign up and log in to the portal.</p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">2</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Start with our 1-month free trial Beta Plan—no upfront payment required.</p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">3</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Download the Portfolio Auditor Tool and copy your unique license key.</p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">4</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Extract the Portfolio Auditor ZIP file to your local drive.</p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">5</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Launch the Portfolio Auditor.exe application and activate it using your license key.</p>
                    </div>
                  </div>

                  {/* Step 6 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">6</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Click the "Step 1" and "Step 2" buttons within the app to install mandatory prerequisites: Python and Power BI Desktop.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Phase 2: Data Import (Investment Upload) */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <button
              onClick={() => togglePhase('phase2')}
              className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-14 h-14 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center mr-4">
                  <Upload className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-slate-900">Phase 2: Data Import (Investment Upload)</h3>
                  <p className="text-sm text-slate-600 mt-1">Step 7: Upload data for assets you own</p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-4">
                {expandedPhase === 'phase2' ? (
                  <ChevronDown className="w-6 h-6 text-green-600" />
                ) : (
                  <ChevronRight className="w-6 h-6 text-slate-400" />
                )}
              </div>
            </button>

            {expandedPhase === 'phase2' && (
              <div className="px-6 py-6 bg-slate-50 border-t border-slate-200">
                {/* Step 7 Intro */}
                <div className="flex items-start mb-6">
                  <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">7</span>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">Click the "Investment Upload" button to begin.</p>
                    <p className="text-sm text-slate-600 mt-1">
                      <strong>Note:</strong> You only need to upload data for the assets you currently own. Follow the specific instructions below for each asset type you hold:
                    </p>
                  </div>
                </div>

                {/* Asset Type Cards */}
                <div className="space-y-4 ml-11">
                  
                  {/* Mutual Fund Card */}
                  <AssetCard
                    title="Mutual Fund Assets"
                    icon={TrendingUp}
                    iconBgColor="bg-purple-100"
                    iconColor="text-purple-600"
                    isExpanded={expandedAsset === 'mutualfund'}
                    onToggle={() => toggleAsset(expandedAsset === 'mutualfund' ? null : 'mutualfund')}
                  >
                    <div className="space-y-3">
                      <p className="font-semibold text-purple-900 text-sm">Downloading CAMS Statement:</p>
                      <ol className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-start">
                          <span className="font-bold text-purple-600 mr-2">1.</span>
                          <div>
                            <strong>Visit the Portal:</strong> Go to the{' '}
                            <a href="https://www.camsonline.com/Investors/Statements/Consolidated-Account-Statement" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 underline">
                              CAMS Investor Page
                            </a>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-purple-600 mr-2">2.</span>
                          <div>
                            <strong>Selection:</strong> Choose the "CAS - CAMS+KFintech" option from the left-side menu to ensure all fund houses are covered.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-purple-600 mr-2">3.</span>
                          <div>
                            <strong>Statement Settings:</strong>
                            <ul className="list-disc ml-5 mt-1 space-y-1 text-slate-600">
                              <li><strong>Type:</strong> Select "Detailed (Includes transaction listing)"</li>
                              <li><strong>Period:</strong> Select "Specific Period." Set the "From Date" to when you first started investing and the "To Date" to today.</li>
                              <li><strong>Folio:</strong> Choose "Without zero balance folios" for active funds, or "With zero balance folios" if you want to include exited investments.</li>
                            </ul>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-purple-600 mr-2">4.</span>
                          <div>
                            <strong>Verification:</strong> Provide your registered Email and PAN (PAN is mandatory).
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-purple-600 mr-2">5.</span>
                          <div>
                            <strong>Security:</strong> Set a Password; you will need to enter this same password when uploading the file to the Portfolio Auditor tool.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-purple-600 mr-2">6.</span>
                          <div>
                            <strong>Delivery:</strong> Click submit and check your email after 5–10 minutes for the PDF.
                          </div>
                        </li>
                      </ol>
                    </div>
                  </AssetCard>

                  {/* Gold / Silver / SGB Card */}
                  <AssetCard
                    title="Gold / Silver / SGB Assets"
                    icon={Coins}
                    iconBgColor="bg-amber-100"
                    iconColor="text-amber-600"
                    isExpanded={expandedAsset === 'gold'}
                    onToggle={() => toggleAsset(expandedAsset === 'gold' ? null : 'gold')}
                  >
                    <div className="space-y-3">
                      <p className="font-semibold text-amber-900 text-sm">Downloading from Zerodha Console:</p>
                      <ol className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-start">
                          <span className="font-bold text-amber-600 mr-2">1.</span>
                          <div>
                            <strong>Login:</strong> Access your{' '}
                            <a href="https://console.zerodha.com" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-amber-800 underline">
                              Zerodha Console
                            </a>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-amber-600 mr-2">2.</span>
                          <div>
                            <strong>Navigate:</strong> Go to the 'Reports' tab and select 'Tradebook'.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-amber-600 mr-2">3.</span>
                          <div>
                            <strong>Filter:</strong> Set the Segment to 'EQ'.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-amber-600 mr-2">4.</span>
                          <div>
                            <strong>Date Range:</strong> Select a window of maximum 1 year. If your investments span multiple years, repeat the download for each 1-year window until you reach the current date.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-amber-600 mr-2">5.</span>
                          <div>
                            <strong>Download:</strong> After clicking 'Submit', click the 'CSV' button to save the file.
                          </div>
                        </li>
                      </ol>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                        <p className="text-xs text-amber-800">
                          <strong>Note:</strong> The tool specifically imports transactions where the symbol starts with &apos;SGB&apos;.
                        </p>
                      </div>
                      
                      {/* Important Note for Physical Gold/Silver */}
                      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mt-3">
                        <p className="font-semibold text-blue-900 text-sm mb-2">Important:</p>
                        <ul className="space-y-2 text-sm text-blue-800">
                          <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span><strong>Physical Gold, Silver &amp; SGB:</strong> For physical holdings, you can add transactions manually within the Portfolio Auditor tool.</span>
                          </li>
                          <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span><strong>SGB in Demat Format:</strong> If you hold SGB in demat format, follow the Zerodha Tradebook download steps above to get your statement and upload it.</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AssetCard>

                  {/* NPS Card */}
                  <AssetCard
                    title="NPS Assets"
                    icon={PiggyBank}
                    iconBgColor="bg-teal-100"
                    iconColor="text-teal-600"
                    isExpanded={expandedAsset === 'nps'}
                    onToggle={() => toggleAsset(expandedAsset === 'nps' ? null : 'nps')}
                  >
                    <div className="space-y-3">
                      <p className="font-semibold text-teal-900 text-sm">Downloading from eNPS Portal:</p>
                      <ol className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-start">
                          <span className="font-bold text-teal-600 mr-2">1.</span>
                          <div>
                            <strong>Login:</strong> Visit the{' '}
                            <a href="https://enps.nps-proteantech.in/eNPS/NationalPensionSystem.html" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 underline">
                              eNPS Protean Website
                            </a>{' '}
                            and log into your account.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-teal-600 mr-2">2.</span>
                          <div>
                            <strong>Navigate:</strong> Go to "Account Statement" and then click "Transaction Statement".
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-teal-600 mr-2">3.</span>
                          <div>
                            <strong>Details Required:</strong>
                            <ul className="list-disc ml-5 mt-1 space-y-1 text-slate-600">
                              <li><strong>Financial Year:</strong> Select the year of your first NPS transaction.</li>
                              <li><strong>Quarter:</strong> Select "ALL".</li>
                              <li><strong>Tier Type:</strong> Choose "Tier I" (or Tier II if applicable).</li>
                            </ul>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-teal-600 mr-2">4.</span>
                          <div>
                            <strong>Generate:</strong> Click "Generate Statement" to load the data.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-teal-600 mr-2">5.</span>
                          <div>
                            <strong>Download:</strong> Click the "Excel" icon in the right corner to save the statement in CSV format.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-bold text-teal-600 mr-2">6.</span>
                          <div>
                            <strong>Repeat:</strong> Repeat this process for every financial year until the current year, as the portal only allows 1-year downloads at a time.
                          </div>
                        </li>
                      </ol>
                    </div>
                  </AssetCard>

                </div>
              </div>
            )}
          </div>

          {/* Phase 3: Analysis & Reporting */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <button
              onClick={() => togglePhase('phase3')}
              className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-violet-600 rounded-xl flex items-center justify-center mr-4">
                  <BarChart className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-slate-900">Phase 3: Analysis & Reporting</h3>
                  <p className="text-sm text-slate-600 mt-1">Steps 8-9: Calculate metrics and view Power BI reports</p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-4">
                {expandedPhase === 'phase3' ? (
                  <ChevronDown className="w-6 h-6 text-purple-600" />
                ) : (
                  <ChevronRight className="w-6 h-6 text-slate-400" />
                )}
              </div>
            </button>

            {expandedPhase === 'phase3' && (
              <div className="px-6 py-6 bg-slate-50 border-t border-slate-200">
                <div className="space-y-5">
                  {/* Step 8 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">8</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Post Investment Upload, click "Daily Refresh"</p>
                      <p className="text-sm text-slate-600 mt-1">
                        This calculates your portfolio's current value, returns, XIRR%, taxation, transaction summary, and compares it against benchmarks. Refer to the Live Demo video for a detailed breakdown of these metrics.
                      </p>
                    </div>
                  </div>

                  {/* Step 9 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">9</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Open Power BI Report and Refresh</p>
                      <p className="text-sm text-slate-600 mt-1 mb-3">
                        Once the daily refresh is complete for all respective assets, click "Open Power BI Report." Once the Power BI report opens, click the "Refresh" button on the top tool section.
                      </p>
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg mb-3">
                        <p className="text-sm text-amber-800">
                          <strong>Note:</strong> During the refresh, you may be prompted for permission to run several times (typically 6-7 times); please click "Run" each time.
                        </p>
                      </div>
                      <p className="text-sm text-slate-600">
                        Once the refresh is completed, all your portfolio metrics will be visible in the report. Navigate through each section to understand your investment portfolio in detail. Refer to the Live Demo video where each report section is explained comprehensively.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Phase 4: Daily Activity */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <button
              onClick={() => togglePhase('phase4')}
              className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-14 h-14 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl flex items-center justify-center mr-4">
                  <RefreshCw className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-slate-900">Phase 4: Daily Activity</h3>
                  <p className="text-sm text-slate-600 mt-1">Step 10: Keep your portfolio up-to-date</p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-4">
                {expandedPhase === 'phase4' ? (
                  <ChevronDown className="w-6 h-6 text-teal-600" />
                ) : (
                  <ChevronRight className="w-6 h-6 text-slate-400" />
                )}
              </div>
            </button>

            {expandedPhase === 'phase4' && (
              <div className="px-6 py-6 bg-slate-50 border-t border-slate-200">
                <div className="space-y-4">
                  {/* Step 10 */}
                  <div className="flex items-start">
                    <span className="flex-shrink-0 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-3 text-sm">10</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Daily Refresh Routine</p>
                      <p className="text-sm text-slate-600 mt-1">
                        As a daily activity, simply run the Portfolio Auditor Application and click the "Daily Refresh" button. This will download the latest NAVs for each asset and recalculate current day market value and other metrics.
                      </p>
                      <p className="text-sm text-slate-600 mt-2">
                        Then, open the Power BI report and click the "Refresh" button to view the updated market value.
                      </p>
                    </div>
                  </div>

                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 ml-11">
                    <p className="text-sm text-teal-800">
                      <strong>✅ That's it!</strong> Your portfolio is now updated with the latest market values.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Troubleshooting Tips Section */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-red-200">
            <button
              onClick={() => togglePhase('troubleshoot')}
              className="w-full px-6 py-5 flex items-center justify-between hover:bg-red-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-14 h-14 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center mr-4">
                  <AlertCircle className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-red-900">Troubleshooting Tips</h3>
                  <p className="text-sm text-red-700 mt-1">Common issues and solutions when working with Power BI</p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-4">
                {expandedPhase === 'troubleshoot' ? (
                  <ChevronDown className="w-6 h-6 text-red-600" />
                ) : (
                  <ChevronRight className="w-6 h-6 text-slate-400" />
                )}
              </div>
            </button>

            {expandedPhase === 'troubleshoot' && (
              <div className="px-6 py-6 bg-red-50 border-t border-red-200">
                <div className="bg-white rounded-xl p-5 border border-red-200">
                  <p className="font-bold text-red-900 text-lg mb-2">Issue: Login Popup Appearing Every 5 Seconds</p>
                  <p className="text-sm text-slate-700 mb-4">
                    If you're getting a login popup repeatedly (every 5 seconds) when refreshing the Power BI report or navigating between pages, follow these steps:
                  </p>
                  
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <p className="font-semibold text-slate-900 mb-3">Solution Steps:</p>
                    <ol className="list-decimal ml-5 space-y-2 text-sm text-slate-700">
                      <li><strong>Click "File" option</strong> in Power BI Desktop menu bar (top-left corner)</li>
                      <li><strong>Click "Options and settings"</strong> from the dropdown menu</li>
                      <li><strong>Click "Options"</strong> to open the Options dialog</li>
                      <li>
                        <strong>Under "Preview features"</strong> section (on the left sidebar):
                        <ul className="list-disc ml-5 mt-1 text-slate-600">
                          <li>Find the option <strong>"Translytical task flow"</strong></li>
                          <li><strong>Uncheck</strong> this option</li>
                        </ul>
                      </li>
                      <li><strong>Click "OK"</strong> to save changes</li>
                      <li><strong>Close Power BI Desktop</strong> completely</li>
                      <li><strong>Reopen the report</strong> - The login popup issue should be resolved</li>
                    </ol>
                  </div>

                  {/* Reference Image */}
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-300">
                    <p className="text-sm font-semibold text-slate-900 mb-2">📸 Reference Screenshot:</p>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_licenseflow-5/artifacts/g22hed5s_image.png"
                      alt="Power BI Options - Translytical task flow setting"
                      className="w-full rounded border border-slate-300 shadow-sm"
                    />
                    <p className="text-xs text-slate-600 mt-2 italic">
                      Location: File → Options and settings → Options → Preview features → Uncheck "Translytical task flow"
                    </p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-green-800">
                      ✅ <strong>After applying this fix:</strong> The login popup will stop appearing, and you can work smoothly with your Power BI report.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Need Help Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-bold mb-3">Need Help with Installation?</h3>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">
            Our support team is here to assist you with any setup issues or questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.Tawk_API && window.Tawk_API.maximize()}
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
            >
              Start Live Chat
            </button>
            <a
              href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}`}
              className="bg-blue-800 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-900 transition-colors shadow-lg"
            >
              Email Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm">
            © 2024 Portfolio Auditor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InstallationGuide;
